import React, { type ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth';
import { checkScreenIpAccess } from '../../api/firebase/function/security/securityIpFunction';
import { isCompanyAdmin, isOperatorStaff } from '../../utils/permission';
import { isSecurityGatePublicPath } from '../../utils/securityGatePublicPath';
import IpRestricted from '../pages/common/IpRestricted';

/**
 * 画面 IP 制限の 403 ゲート（#2982）。
 * ログイン中ユーザーの送信元 IP がテナントの許可リスト外の場合、
 * 通常画面の代わりに 403 画面（IpRestricted）を表示する。
 *
 * - 判定はサーバー側（checkScreenIpAccess callable）で行い、ログインユーザーが
 *   変わったタイミングで 1 回実行する（データ経路は各 callable 側の共通ガードが遮断）
 * - 運営スタッフ（admin / operator）は対象外（代理ログイン・ロックアウト救済を止めない）
 * - 企業管理者（companyAdmin）は暫定的に対象外（サーバー側ガードと同一の暫定対応。
 *   2026-08-20 の方針確定まで）
 * - 判定はノンブロッキング（結果待ちで全ユーザーの初期表示を遅らせない）。
 *   判定前に発行された API 呼び出しはサーバー側ガードで拒否される
 * - 判定 callable 自体の失敗時は表示を許可する（サーバー側 enforcement が最終防衛線）
 */
const IpRestrictionGate = ({ children }: { children: ReactNode }): JSX.Element => {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const [blocked, setBlocked] = useState(false);

  const uid = user?.uid ?? null;
  const isExempt = isOperatorStaff(user?.role) || isCompanyAdmin(user?.role);

  useEffect(() => {
    // 未ログイン・運営スタッフ・企業管理者（暫定対応）は判定しない（ブロック状態も解除）
    if (!uid || isExempt) {
      setBlocked(false);
      return undefined;
    }
    let cancelled = false;
    checkScreenIpAccess()
      .then(result => {
        if (!cancelled) setBlocked(!result.allowed);
      })
      .catch(() => {
        // 判定不能時は表示を許可（サーバー側の共通ガードが最終防衛線）
        if (!cancelled) setBlocked(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, isExempt]);

  if (blocked && !isSecurityGatePublicPath(pathname)) {
    return <IpRestricted />;
  }

  return <>{children}</>;
};

export default IpRestrictionGate;
